import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject, useProjectTools, useProjectCollaborators, useUpdateProject, useDeleteProject, useSyncProjectCollaborators, useSyncProjectTools, useSearchProfiles, solutionTypeToKey, solutionKeyToInt } from '@/hooks/useProjects';
import { useSelectedCohort } from '@/hooks/useSelectedCohort';
import { useAuth } from '@/contexts/AuthContext';
import { useAIToolsCatalog } from '@/hooks/useAIToolsCatalog';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableCombobox } from '@/components/ui/searchable-combobox';
import { toast } from 'sonner';
import { 
  ArrowLeft, Target, Bot, Sparkles, FileText, Send, Layout, Loader2, Users,
  Save, Upload, File, CheckCircle2, X, Eye, Play, BookOpen, ClipboardCheck, Trophy,
  Code, Zap, BarChart3, Database, UserPlus, Search, Wrench, TrendingUp as TrendingUpIcon,
  ChevronDown, Trash2, AlertTriangle, Download, RefreshCw, Building2, Calendar, Globe, GraduationCap,
  HelpCircle, Info, Lightbulb
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RoiHelpDialog } from '@/components/participant/RoiHelpDialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { RubricAnswers } from '@/components/ProjectRubricForm';
import { LIDER_IA_COMPONENTS, DIMENSION_COLORS } from '@/data/liderIAEvaluationData';
import { ROICostStructure, ROIBenefitItem, BenefitType, COST_LABELS, BENEFIT_TYPE_LABELS, BENEFIT_TYPE_DESCRIPTIONS, calculateTotalCosts, calculateTotalBenefits, calculateROI, calculateBCR, calculatePaybackMonths } from '@/data/roiModelData';
import { DollarSign } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ProjectSuggestionsPanel } from '@/components/projects/ProjectSuggestionsPanel';
import { useGenerateProjectSuggestions } from '@/hooks/useProjectSuggestions';

const SOLUTION_TYPES_CONFIG = {
  content: { id: 'content', label: 'Creación de Contenido con IA', icon: FileText, color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  vibecoding: { id: 'vibecoding', label: 'Vibecoding y Creación de Apps con IA', icon: Code, color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
  assistant: { id: 'assistant', label: 'Asistente/ Agente IA', icon: Bot, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  automation: { id: 'automation', label: 'Automatización IA', icon: Zap, color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
  data: { id: 'data', label: 'Análisis de Datos con IA', icon: BarChart3, color: 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20' },
};

type SolutionTypeKey = keyof typeof SOLUTION_TYPES_CONFIG;

const statusConfig: Record<string, { label: string; color: string }> = {
  ideation: { label: 'Ideación', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  in_progress: { label: 'En progreso', color: 'bg-primary/10 text-primary border-primary/20' },
  completed: { label: 'Completado', color: 'bg-success/10 text-success border-success/20' },
  paused: { label: 'Pausado', color: 'bg-muted/50 text-muted-foreground border-border' },
};

const STATUS_OPTIONS = [
  { value: 'ideation', label: 'Ideación' },
  { value: 'in_progress', label: 'En progreso' },
  { value: 'completed', label: 'Completado' },
  { value: 'paused', label: 'Pausado' },
];

interface UploadedFile {
  id: string;
  url?: string;
  name: string;
  size: string;
  type: string;
  uploadedAt: string;
  status: 'pending' | 'approved' | 'rejected';
}

export function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState('info');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const WELCOME_MSG = { role: 'assistant' as const, content: '¡Hola! Soy tu asistente de prototipado. Estoy aquí para ayudarte a crear el prototipo de tu proyecto. ¿Por dónde te gustaría empezar?' };
  const [chatHistory, setChatHistory] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([WELCOME_MSG]);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);

  // Real data hooks
  const { data: projectRow, isLoading: projectLoading } = useProject(projectId);
  const { data: toolsData } = useProjectTools(projectId);
  const { data: collaboratorsData } = useProjectCollaborators(projectId);
  const { data: catalogTools = [] } = useAIToolsCatalog();
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const syncCollaborators = useSyncProjectCollaborators();
  const syncTools = useSyncProjectTools();
  const generateSuggestions = useGenerateProjectSuggestions();

  // User diagnostic level
  const { data: diagnosticData } = useQuery({
    queryKey: ['user-diagnostic-level', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase.from('participant_diagnostics').select('maturity_level').eq('user_id', user.id).maybeSingle();
      return data?.maturity_level ?? null;
    },
    enabled: !!user,
  });

  // Templates from DB
  const solutionTypeInt = projectRow ? projectRow.solution_type : null;
  const { data: dbTemplates = [] } = useQuery({
    queryKey: ['templates-for-project', solutionTypeInt],
    queryFn: async () => {
      let q = supabase.from('templates').select('*').order('usage_count', { ascending: false });
      if (solutionTypeInt) {
        q = q.contains('solution_types', [solutionTypeInt]);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  // Map DB row to local shape
  const project = projectRow ? {
    id: projectRow.id,
    title: projectRow.name,
    status: (projectRow.status || 'ideation') as string,
    solutionType: solutionTypeToKey(projectRow.solution_type) as SolutionTypeKey,
    problem: projectRow.problem || '',
    targetAudience: projectRow.target_audience || '',
    dataUsed: projectRow.data_used || '',
    createdAt: projectRow.created_at?.split('T')[0] || '',
  } : null;

  // Editable form state
  const [formData, setFormData] = useState({
    title: '', solutionType: '', problem: '', solutionDescription: '',
    targetAudience: '', dataUsed: '', selectedTools: [] as string[],
    kpiName: '', kpiCurrentValue: '', kpiTargetValue: '',
    cohortId: '' as string, isOrgVisible: false, isPublic: false,
    status: 'ideation',
  });
  const [invitedUsers, setInvitedUsers] = useState<Array<{ id: string; name: string; email?: string }>>([]);

  // Sync form when project loads
  useEffect(() => {
    if (projectRow) {
      setFormData(prev => ({
        ...prev,
        title: projectRow.name || '',
        solutionType: solutionTypeToKey(projectRow.solution_type),
        problem: projectRow.problem || '',
        solutionDescription: projectRow.solution_description || '',
        targetAudience: projectRow.target_audience || '',
        dataUsed: projectRow.data_used || '',
        selectedTools: toolsData?.map((t: any) => t.tool_name) || prev.selectedTools,
        kpiName: projectRow.kpi_name || '',
        kpiCurrentValue: projectRow.kpi_current_value || '',
        kpiTargetValue: projectRow.kpi_target_value || '',
        cohortId: projectRow.cohort_id || '',
        isOrgVisible: projectRow.is_org_visible || false,
        isPublic: projectRow.is_public || false,
        status: projectRow.status || 'ideation',
      }));
      const roiData = (projectRow as any).roi_data;
      if (roiData) {
        if (roiData.costs) setRoiCosts(roiData.costs);
        if (roiData.benefits && roiData.benefits.length > 0) setRoiBenefits(roiData.benefits);
      }
      // Restore deliverable files: prefer file_urls (multi), fallback to file_url (legacy)
      const filesArr = Array.isArray((projectRow as any).file_urls) ? (projectRow as any).file_urls : [];
      if (filesArr.length > 0) {
        setDeliverableFiles(filesArr.map((f: any, idx: number) => ({
          id: `db-${idx}`,
          url: f.url,
          name: f.name || (typeof f.url === 'string' ? f.url.split('/').pop() : 'archivo') || 'archivo',
          size: f.size || '',
          type: '',
          uploadedAt: f.uploaded_at || projectRow.submitted_at?.split('T')[0] || '',
          status: 'pending',
        })));
      } else if (projectRow.file_url) {
        const fileName = projectRow.file_url.split('/').pop() || 'archivo';
        setDeliverableFiles([{ id: 'db-legacy', url: projectRow.file_url, name: fileName, size: '', type: '', uploadedAt: projectRow.submitted_at?.split('T')[0] || '', status: 'pending' }]);
      }
    }
  }, [projectRow, toolsData]);

  // Sync collaborators from DB
  useEffect(() => {
    if (collaboratorsData) {
      setInvitedUsers(collaboratorsData);
    }
  }, [collaboratorsData]);

  // User search for collaborators
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const { data: searchResults = [] } = useSearchProfiles(userSearchQuery, profile?.organization_id);
  const filteredUsers = searchResults.filter(u => !invitedUsers.some(inv => inv.id === u.id) && u.id !== user?.id);

  const handleAddUser = (u: { id: string; full_name: string; email: string }) => {
    setInvitedUsers(prev => [...prev, { id: u.id, name: u.full_name, email: u.email }]);
    setUserSearchQuery('');
    setShowUserSearch(false);
    toast.success(`${u.full_name} agregado como colaborador`);
  };
  const handleRemoveUser = (userId: string) => {
    setInvitedUsers(prev => prev.filter(u => u.id !== userId));
  };

  // Tools dropdown
  
  const handleAddTool = (toolName: string) => {
    if (!formData.selectedTools.includes(toolName)) {
      setFormData(prev => ({ ...prev, selectedTools: [...prev.selectedTools, toolName] }));
      toast.success(`${toolName} agregado`);
    }
  };
  const handleRemoveTool = (toolName: string) => {
    setFormData(prev => ({ ...prev, selectedTools: prev.selectedTools.filter(n => n !== toolName) }));
  };

  // Deliverable
  const [deliverableFiles, setDeliverableFiles] = useState<UploadedFile[]>([]);
  const [rubricAnswers, setRubricAnswers] = useState<RubricAnswers>({});
  const [deliverableSubmitted, setDeliverableSubmitted] = useState(false);
  const { enrollments, selectedCohortId: globalCohortId } = useSelectedCohort();
  // Only guided (non self-paced) cohorts can be linked to a project
  const guidedEnrollments = enrollments.filter(e => !e.view_only && !e.is_perpetual);
  const [submitCohortId, setSubmitCohortId] = useState('');

  useEffect(() => {
    if (projectRow) {
      if (projectRow.rubric_answers && Object.keys(projectRow.rubric_answers).length > 0) setRubricAnswers(projectRow.rubric_answers as RubricAnswers);
      if (projectRow.submitted) setDeliverableSubmitted(true);
      const globalIsGuided = guidedEnrollments.some(e => e.cohort_id === globalCohortId);
      setSubmitCohortId(projectRow.cohort_id || (globalIsGuided ? globalCohortId : '') || '');
    }
  }, [projectRow, globalCohortId]);

  // ROI
  const [roiHelpOpen, setRoiHelpOpen] = useState(false);
  const [roiCosts, setRoiCosts] = useState<ROICostStructure>({ subscriptions: 0, tokenConsumption: 0, personnel: 0, training: 0 });
  const [roiBenefits, setRoiBenefits] = useState<ROIBenefitItem[]>([{ type: 'time_savings', amount: 0, description: '' }]);
  const handleAddBenefit = () => setRoiBenefits(prev => [...prev, { type: 'other' as BenefitType, amount: 0, description: '' }]);
  const handleRemoveBenefit = (i: number) => setRoiBenefits(prev => prev.filter((_, idx) => idx !== i));
  const handleBenefitChange = (i: number, field: keyof ROIBenefitItem, value: string | number) => setRoiBenefits(prev => prev.map((b, idx) => idx === i ? { ...b, [field]: value } : b));
  const roiTotalCosts = calculateTotalCosts(roiCosts);
  const roiTotalBenefits = calculateTotalBenefits(roiBenefits);
  const roiPercentage = calculateROI(roiTotalBenefits, roiTotalCosts);
  const roiPayback = calculatePaybackMonths(roiTotalBenefits, roiTotalCosts);

  // Scroll chat to bottom
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatHistory]);

  // Load persisted prototype chat (single continuous conversation per project)
  useEffect(() => {
    if (!projectId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: sess } = await supabase
          .from('chat_sessions')
          .select('id')
          .eq('user_id', user.id)
          .eq('context_type', 'prototype')
          .eq('context_id', projectId)
          .maybeSingle();
        if (cancelled) return;
        if (sess?.id) {
          setChatSessionId(sess.id);
          const { data: msgs } = await supabase
            .from('chat_messages')
            .select('role, content')
            .eq('session_id', sess.id)
            .order('created_at', { ascending: true });
          if (cancelled) return;
          if (msgs && msgs.length > 0) {
            setChatHistory(msgs.filter(m => m.role === 'user' || m.role === 'assistant').map(m => ({ role: m.role as 'user' | 'assistant', content: m.content })));
          }
        }
      } catch (e) {
        console.error('Error loading prototype chat history', e);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, user?.id]);

  if (projectLoading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }
  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <p className="text-muted-foreground">Proyecto no encontrado</p>
        <Button onClick={() => navigate('/challenge')} variant="outline"><ArrowLeft className="w-4 h-4 mr-2" />Volver</Button>
      </div>
    );
  }

  const hasDeliverable = () =>
    deliverableFiles.length > 0 ||
    !!projectRow?.file_url ||
    (Array.isArray((projectRow as any)?.file_urls) && (projectRow as any).file_urls.length > 0);

  // Un proyecto enviado con entregable siempre queda como "Completado"
  const isLockedCompleted = () => !!projectRow?.submitted && hasDeliverable();

  const handleFormChange = (field: string, value: string | boolean) => {
    if (field === 'status' && value === 'completed' && !hasDeliverable()) {
      toast.error('Para marcar como Completado debes cargar al menos un entregable.');
      return;
    }
    if (field === 'status' && value !== 'completed' && isLockedCompleted()) {
      toast.error('El proyecto ya fue enviado con entregable, por lo que permanece como Completado.');
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };


  const handleSaveProject = async () => {
    if (!projectId) return;
    setIsSaving(true);
    try {
      await updateProject.mutateAsync({
        id: projectId,
        name: formData.title,
        problem: formData.problem,
        solution_description: formData.solutionDescription,
        target_audience: formData.targetAudience,
        data_used: formData.dataUsed,
        solution_type: solutionKeyToInt(formData.solutionType),
        kpi_name: formData.kpiName,
        kpi_current_value: formData.kpiCurrentValue,
        kpi_target_value: formData.kpiTargetValue,
        status: (isLockedCompleted() ? 'completed' : formData.status) as any,
        cohort_id: formData.cohortId || null,
        is_org_visible: formData.isOrgVisible,
        is_public: formData.isPublic,
        roi_data: { costs: roiCosts, benefits: roiBenefits },
      } as any);
      // Sync collaborators
      await syncCollaborators.mutateAsync({ projectId, userIds: invitedUsers.map(u => u.id) });
      // Sync tools
      await syncTools.mutateAsync({ projectId, toolNames: formData.selectedTools });
      // Trigger AI suggestions regeneration in background
      generateSuggestions.mutate({ projectId, force: true });
      toast.success('Proyecto guardado exitosamente');
    } catch (err: any) {
      toast.error(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  // Real AI chat
  const handleSendMessage = async () => {
    if (!message.trim()) return;
    const userMessage = message;
    setMessage('');
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    // Ensure persisted session exists for this project
    let sessionId = chatSessionId;
    try {
      if (!sessionId && user && projectId) {
        const { data: created, error: createErr } = await supabase
          .from('chat_sessions')
          .insert({
            user_id: user.id,
            context_type: 'prototype',
            context_id: projectId,
            title: 'Asistente de Prototipado',
          })
          .select('id')
          .single();
        if (createErr) throw createErr;
        sessionId = created.id;
        setChatSessionId(sessionId);
      }
      if (sessionId) {
        await supabase.from('chat_messages').insert({ session_id: sessionId, role: 'user', content: userMessage });
      }
    } catch (e) {
      console.error('Error persisting user message', e);
    }

    let assistantSoFar = '';
    const chatUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/prototype-assistant`;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        toast.error('Sesión expirada, vuelve a iniciar sesión.');
        setIsLoading(false);
        return;
      }
      const resp = await fetch(chatUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          messages: [...chatHistory, { role: 'user', content: userMessage }].map(m => ({ role: m.role, content: m.content })),
          projectContext: {
            name: formData.title, solutionType: formData.solutionType, problem: formData.problem,
            targetAudience: formData.targetAudience, dataUsed: formData.dataUsed,
            solutionDescription: formData.solutionDescription, tools: formData.selectedTools,
            kpiName: formData.kpiName, kpiCurrentValue: formData.kpiCurrentValue, kpiTargetValue: formData.kpiTargetValue,
            diagnosticLevel: diagnosticData ?? null,
          },
        }),
      });

      if (!resp.ok || !resp.body) {
        if (resp.status === 429) { toast.error('Límite de solicitudes excedido, intenta en unos segundos.'); }
        else if (resp.status === 402) { toast.error('Créditos agotados.'); }
        else { toast.error('Error del asistente IA'); }
        setIsLoading(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });
        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') { streamDone = true; break; }
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantSoFar += content;
              setChatHistory(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant' && prev.length > 1 && prev[prev.length - 2]?.role === 'user') {
                  return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: assistantSoFar } : m);
                }
                return [...prev, { role: 'assistant', content: assistantSoFar }];
              });
            }
          } catch { textBuffer = line + '\n' + textBuffer; break; }
        }
      }

      // Persist final assistant message
      if (sessionId && assistantSoFar) {
        try {
          await supabase.from('chat_messages').insert({ session_id: sessionId, role: 'assistant', content: assistantSoFar });
        } catch (e) {
          console.error('Error persisting assistant message', e);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Error de conexión con el asistente');
    }
    setIsLoading(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const handleFileUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.doc,.docx,.pptx,.xlsx,.zip,.png,.jpg,.jpeg';
    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length === 0 || !projectId || !user) return;
      try {
        const { sanitizeFilename } = await import('@/lib/sanitizeFilename');
        const uploaded: UploadedFile[] = [];
        for (const file of files) {
          const filePath = `${user.id}/${Date.now()}_${sanitizeFilename(file.name)}`;
          const { error } = await supabase.storage.from('deliverables').upload(filePath, file);
          if (error) throw error;
          const { data: urlData } = await supabase.storage.from('deliverables').createSignedUrl(filePath, 60 * 60 * 24 * 365);
          const signedUrl = urlData?.signedUrl || '';
          uploaded.push({
            id: filePath,
            url: signedUrl,
            name: file.name,
            size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
            type: file.type,
            uploadedAt: new Date().toLocaleDateString('es-ES'),
            status: 'pending',
          });
        }
        const next = [...deliverableFiles, ...uploaded];
        setDeliverableFiles(next);
        // Persist: file_urls (full list) + file_url (first, backward compat)
        await updateProject.mutateAsync({
          id: projectId,
          file_urls: next.map(f => ({ url: f.url, name: f.name, size: f.size, uploaded_at: f.uploadedAt })),
          file_url: next[0]?.url || null,
        } as any);
        toast.success(`${uploaded.length} archivo(s) cargado(s)`);
      } catch (err: any) { toast.error(err.message || 'Error al subir archivo'); }
    };
    input.click();
  };

  const handleRemoveDeliverableFile = async (id: string) => {
    const next = deliverableFiles.filter(f => f.id !== id);
    setDeliverableFiles(next);
    if (projectId) {
      try {
        const updates: any = {
          id: projectId,
          file_urls: next.map(f => ({ url: f.url, name: f.name, size: f.size, uploaded_at: f.uploadedAt })),
          file_url: next[0]?.url || null,
        };
        // Coherencia inversa: si quedó sin entregables y estaba completado, bajar a in_progress
        if (next.length === 0 && formData.status === 'completed') {
          updates.status = 'in_progress';
          setFormData(prev => ({ ...prev, status: 'in_progress' }));
        }
        await updateProject.mutateAsync(updates);
      } catch (e) { /* noop */ }
    }
    toast.info('Archivo eliminado');
  };

  const handleRubricAnswerChange = (qId: string, val: string) => setRubricAnswers(prev => ({ ...prev, [qId]: val }));

  const handleSaveDraft = async () => {
    if (!projectId) return;
    try {
      await updateProject.mutateAsync({
        id: projectId,
        rubric_answers: rubricAnswers as any,
        roi_data: { costs: roiCosts, benefits: roiBenefits },
      } as any);
      toast.success(deliverableSubmitted ? 'Cambios guardados exitosamente' : 'Borrador guardado exitosamente');
    } catch (err: any) { toast.error(err.message || 'Error al guardar'); }
  };

  const handleSubmitDeliverable = async () => {
    if (!projectId) return;
    if (!hasDeliverable()) {
      toast.error('Debes cargar al menos un archivo antes de enviar el entregable.');
      return;
    }
    try {
      const updateData: any = {
        id: projectId,
        rubric_answers: rubricAnswers as any,
        roi_data: { costs: roiCosts, benefits: roiBenefits },
        submitted: true,
        submitted_at: new Date().toISOString(),
        status: 'completed',
      };
      if (!projectRow?.cohort_id && submitCohortId) updateData.cohort_id = submitCohortId;
      await updateProject.mutateAsync(updateData);
      setDeliverableSubmitted(true);
      setFormData(prev => ({ ...prev, status: 'completed' }));
      toast.success('Entregable enviado a revisión');
      // Trigger AI evaluation in background (fire-and-forget)
      toast.info('Evaluación IA en proceso…');
      supabase.functions
        .invoke('evaluate-project', { body: { project_id: projectId } })
        .then(({ error }) => {
          if (error) console.error('[evaluate-project] failed:', error);
        })
        .catch((err) => console.error('[evaluate-project] failed:', err));
    } catch (err: any) { toast.error(err.message || 'Error al enviar'); }
  };

  const handleUseTemplate = async (template: any) => {
    const kind = template.resource_type || (template.external_url ? 'link' : template.file_url ? 'download' : 'copy');
    if (kind === 'link' && template.external_url) { window.open(template.external_url, '_blank'); }
    else if (kind === 'download' && template.file_url) { window.open(template.file_url, '_blank'); }
    else if (template.content) { await navigator.clipboard.writeText(template.content); toast.success('Contenido copiado al portapapeles'); }
    // Increment usage
    supabase.from('templates').update({ usage_count: (template.usage_count || 0) + 1 }).eq('id', template.id).then();
  };

  const stCfg = statusConfig[project.status] || statusConfig.ideation;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/challenge')} className="shrink-0">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center flex-wrap gap-3 mb-2">
            <h1 className="text-2xl font-bold text-foreground">{project.title}</h1>
            {/* Status Selector */}
            <Select value={formData.status} onValueChange={(v) => handleFormChange('status', v)}>
              <SelectTrigger className={`w-auto gap-2 h-8 px-3 text-xs font-semibold border rounded-full ${stCfg.color}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value} disabled={s.value === 'completed' && !hasDeliverable()}>{s.label}{s.value === 'completed' && !hasDeliverable() ? ' (requiere entregable)' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            {project.solutionType && SOLUTION_TYPES_CONFIG[project.solutionType] && (
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${SOLUTION_TYPES_CONFIG[project.solutionType].color}`}>
                {(() => { const SolIcon = SOLUTION_TYPES_CONFIG[project.solutionType].icon; return <SolIcon className="w-3 h-3" />; })()}
                {SOLUTION_TYPES_CONFIG[project.solutionType].label.split(' ')[0]}
              </span>
            )}
          </div>
          <p className="text-muted-foreground text-sm line-clamp-2">{project.problem}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="info" className="gap-2"><FileText className="w-4 h-4" /><span className="hidden sm:inline">Información</span></TabsTrigger>
          <TabsTrigger value="prototype" className="gap-2"><Bot className="w-4 h-4" /><span className="hidden sm:inline">Prototipo</span></TabsTrigger>
          <TabsTrigger value="templates" className="gap-2"><Layout className="w-4 h-4" /><span className="hidden sm:inline">Contenido y Recursos</span></TabsTrigger>
          <TabsTrigger value="deliverable" className="gap-2"><Upload className="w-4 h-4" /><span className="hidden sm:inline">Entregable</span></TabsTrigger>
        </TabsList>

        {/* ===== INFO TAB ===== */}
        <TabsContent value="info" className="space-y-6">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-primary" />Información del Proyecto</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Solution Type */}
              <div className="space-y-2">
                <Label>Tipo de Solución *</Label>
                <Select value={formData.solutionType} onValueChange={(v) => handleFormChange('solutionType', v)}>
                  <SelectTrigger><SelectValue placeholder="Selecciona tipo" /></SelectTrigger>
                  <SelectContent>{Object.values(SOLUTION_TYPES_CONFIG).map(t => { const I = t.icon; return <SelectItem key={t.id} value={t.id}><div className="flex items-center gap-2"><I className="w-4 h-4" />{t.label}</div></SelectItem>; })}</SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2"><Label>Título *</Label><Input value={formData.title} onChange={e => handleFormChange('title', e.target.value)} /></div>
                <div className="space-y-2"><Label className="flex items-center gap-2"><Calendar className="w-4 h-4 text-muted-foreground" />Fecha</Label><Input value={new Date(project.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })} disabled className="bg-muted/50" /></div>
                <div className="space-y-2"><Label>Público Objetivo *</Label><Input value={formData.targetAudience} onChange={e => handleFormChange('targetAudience', e.target.value)} /></div>
              </div>

              <div className="space-y-2"><Label>Problema *</Label><Textarea value={formData.problem} onChange={e => handleFormChange('problem', e.target.value)} rows={3} /></div>
              <div className="space-y-2"><Label>Descripción de la Solución</Label><Textarea value={formData.solutionDescription} onChange={e => handleFormChange('solutionDescription', e.target.value)} rows={3} /></div>
              <div className="space-y-2"><Label>Datos Usados</Label><Textarea value={formData.dataUsed} onChange={e => handleFormChange('dataUsed', e.target.value)} rows={2} /></div>

              {/* Tools from catalog */}
              <div className="space-y-3 p-4 rounded-xl border border-border bg-muted/30">
                <div className="flex items-center gap-2"><Wrench className="w-5 h-5 text-primary" /><span className="font-semibold">Herramientas</span><Badge variant="secondary" className="ml-auto">{formData.selectedTools.length}</Badge></div>
                {formData.selectedTools.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.selectedTools.map(toolName => {
                      const cat = catalogTools.find(t => t.name === toolName);
                      return (
                        <div key={toolName} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-background border border-border">
                          {cat?.logo_url ? <img src={cat.logo_url} className="w-4 h-4 rounded" /> : <Wrench className="w-3 h-3" />}
                          <span className="text-sm font-medium">{toolName}</span>
                          <button onClick={() => handleRemoveTool(toolName)} className="w-5 h-5 rounded-full hover:bg-destructive/10 flex items-center justify-center"><X className="w-3 h-3" /></button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <SearchableCombobox
                  addMode
                  placeholder="Agregar Herramienta"
                  searchPlaceholder="Buscar herramienta..."
                  emptyText="No se encontraron herramientas"
                  triggerIcon={<Wrench className="w-4 h-4" />}
                  options={catalogTools.map(t => ({
                    value: t.name,
                    label: t.name,
                    iconUrl: t.logo_url || undefined,
                    keywords: [t.category, t.description].filter(Boolean) as string[],
                  }))}
                  excludeValues={formData.selectedTools}
                  onSelect={(v) => handleAddTool(v)}
                />
              </div>

              {/* KPI */}
              <div className="space-y-4 p-4 rounded-xl border border-primary/20 bg-primary/5">
                <div className="flex items-center gap-2"><TrendingUpIcon className="w-5 h-5 text-primary" /><span className="font-semibold">KPI de Impacto</span></div>
                <div className="space-y-2"><Label>Nombre del KPI</Label><Input value={formData.kpiName} onChange={e => handleFormChange('kpiName', e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Valor Actual</Label><Input value={formData.kpiCurrentValue} onChange={e => handleFormChange('kpiCurrentValue', e.target.value)} /></div>
                  <div className="space-y-2"><Label>Valor Objetivo</Label><Input value={formData.kpiTargetValue} onChange={e => handleFormChange('kpiTargetValue', e.target.value)} /></div>
                </div>
              </div>

              {/* Collaborators - Real */}
              <div className="space-y-3 p-4 rounded-xl border border-border bg-muted/30">
                <div className="flex items-center gap-2"><Users className="w-5 h-5 text-primary" /><span className="font-semibold">Colaboradores</span><Badge variant="secondary" className="ml-auto">{invitedUsers.length}</Badge></div>
                {invitedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {invitedUsers.map(u => (
                      <div key={u.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-background border border-border">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-[10px] font-bold text-white">{u.name.split(' ').map(n => n[0]).join('')}</div>
                        <span className="text-sm font-medium">{u.name}</span>
                        <button onClick={() => handleRemoveUser(u.id)} className="w-5 h-5 rounded-full hover:bg-destructive/10 flex items-center justify-center"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {showUserSearch ? (
                  <div className="space-y-2">
                    <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input placeholder="Buscar por nombre..." value={userSearchQuery} onChange={e => setUserSearchQuery(e.target.value)} className="pl-9" autoFocus /></div>
                    {userSearchQuery.length >= 2 && filteredUsers.length > 0 && (
                      <div className="border border-border rounded-lg bg-card max-h-40 overflow-y-auto">
                        {filteredUsers.map(u => (
                          <button key={u.id} onClick={() => handleAddUser(u)} className="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-xs font-bold text-white">{u.full_name.split(' ').map(n => n[0]).join('')}</div>
                            <div><div className="font-medium text-sm">{u.full_name}</div><div className="text-xs text-muted-foreground">{u.email}</div></div>
                          </button>
                        ))}
                      </div>
                    )}
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setShowUserSearch(false); setUserSearchQuery(''); }}>Cancelar</Button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setShowUserSearch(true)}><UserPlus className="w-4 h-4" />Agregar Colaborador</Button>
                )}
              </div>

              {/* Association & Visibility */}
              <div className="space-y-4 p-4 rounded-xl border border-border bg-muted/30">
                <div className="flex items-center gap-2"><GraduationCap className="w-5 h-5 text-primary" /><span className="font-semibold">Asociación y Visibilidad</span></div>
                <div className="space-y-2">
                  <Label>Tipo de Proyecto</Label>
                  <div className="flex gap-3">
                    <Button type="button" variant={formData.cohortId ? 'default' : 'outline'} size="sm" className="gap-2" onClick={() => { if (!formData.cohortId && guidedEnrollments.length > 0) handleFormChange('cohortId', guidedEnrollments[0].cohort_id); }}>
                      <GraduationCap className="w-4 h-4" />Proyecto de Cohorte
                    </Button>
                    <Button type="button" variant={!formData.cohortId ? 'default' : 'outline'} size="sm" className="gap-2" onClick={() => handleFormChange('cohortId', '')}>
                      <Globe className="w-4 h-4" />Proyecto Libre
                    </Button>
                  </div>
                </div>
                {formData.cohortId && enrollments.length > 0 && (
                  <div className="space-y-2">
                    <Label>Cohorte</Label>
                    <SearchableCombobox
                      value={formData.cohortId}
                      onSelect={v => handleFormChange('cohortId', v)}
                      placeholder="Selecciona cohorte"
                      searchPlaceholder="Buscar cohorte o ruta..."
                      options={enrollments.filter(e => !e.view_only && (!e.is_perpetual || e.cohort_id === projectRow?.cohort_id)).map(e => ({
                        value: e.cohort_id,
                        label: `Ruta: ${e.program_name} — ${e.cohort_name}`,
                        keywords: [e.program_name, e.cohort_name],
                      }))}
                    />
                    <p className="text-xs text-muted-foreground">Al asociar a una cohorte, el proyecto aparecerá automáticamente en el showcase de esa cohorte.</p>
                  </div>
                )}
                {profile?.organization_id && (
                  <div className="flex items-center justify-between py-2">
                    <div><p className="text-sm font-medium">Visible para mi organización</p><p className="text-xs text-muted-foreground">Permite que los miembros de tu empresa vean este proyecto</p></div>
                    <Switch checked={formData.isOrgVisible} onCheckedChange={c => handleFormChange('isOrgVisible', c as any)} />
                  </div>
                )}
                <div className="flex items-center justify-between py-2">
                  <div><p className="text-sm font-medium">Proyecto público</p><p className="text-xs text-muted-foreground">Visible para todos en la plataforma</p></div>
                  <Switch checked={formData.isPublic} onCheckedChange={c => handleFormChange('isPublic', c as any)} />
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row justify-between gap-4 pt-4 border-t border-border">
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="outline" className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/10"><Trash2 className="w-4 h-4" />Eliminar</Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>¿Eliminar proyecto?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction className="bg-destructive text-destructive-foreground" onClick={async () => { try { await deleteProject.mutateAsync(projectId!); toast.success('Eliminado'); navigate('/challenge'); } catch (err: any) { toast.error(err.message); } }}>Eliminar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button onClick={handleSaveProject} disabled={isSaving} className="gradient-primary gap-2">
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Guardar Cambios
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== PROTOTYPE TAB ===== */}
        <TabsContent value="prototype" className="space-y-6">
          <Card className="glass-card h-[700px] flex flex-col">
            <CardHeader className="pb-4 border-b border-border">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center"><Bot className="w-4 h-4 text-white" /></div>
                Asistente de Prototipado IA
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {chatHistory.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.role === 'user' ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-muted text-foreground rounded-bl-md'}`}>
                      {msg.role === 'assistant' && <div className="flex items-center gap-2 mb-1"><Sparkles className="w-3 h-3 text-primary" /><span className="text-xs font-medium text-primary">Asistente IA</span></div>}
                      {msg.role === 'assistant' ? (
                        <div className="text-sm prose prose-sm max-w-none dark:prose-invert">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && chatHistory[chatHistory.length - 1]?.role !== 'assistant' && (
                  <div className="flex justify-start"><div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3"><div className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-primary" /><span className="text-sm text-muted-foreground">Pensando...</span></div></div></div>
                )}
                <div ref={chatEndRef} />
              </div>
              <div className="px-4 py-2 border-t border-border">
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setMessage('¿Cómo empiezo a diseñar mi prototipo?')}>🎨 Diseñar</Button>
                  
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setMessage('¿Qué herramientas necesito?')}>🛠️ Herramientas</Button>
                </div>
              </div>
              <div className="p-4 border-t border-border">
                <div className="flex gap-2">
                  <Textarea value={message} onChange={e => setMessage(e.target.value)} onKeyDown={handleKeyPress} placeholder="Escribe tu mensaje..." className="resize-none min-h-[44px] max-h-[120px]" rows={1} />
                  <Button onClick={handleSendMessage} disabled={!message.trim() || isLoading} className="gradient-primary shrink-0"><Send className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TEMPLATES + CONTENT TAB ===== */}
        <TabsContent value="templates" className="space-y-6">
          {projectId && (
            <ProjectSuggestionsPanel projectId={projectId} solutionTypeInt={solutionTypeInt} />
          )}
        </TabsContent>

        {/* ===== DELIVERABLE TAB ===== */}
        <TabsContent value="deliverable" className="space-y-6">
          {deliverableSubmitted && (
            <Card className="glass-card border-success/30"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-success" /></div><div><h4 className="font-semibold">Entregable Enviado</h4><p className="text-sm text-muted-foreground">Tu proyecto está pendiente de validación</p></div></div></CardContent></Card>
          )}
          <Card className="glass-card">
            <CardHeader><CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-primary" />Entregable del Proyecto</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              {/* File upload */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <File className="w-4 h-4 text-primary" />
                  Evidencia del proyecto ({deliverableFiles.length})
                  {deliverableFiles.length > 0 && <CheckCircle2 className="w-4 h-4 text-success" />}
                </Label>
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
                  <p className="text-sm">
                    Sube un documento (Word, PDF o PowerPoint) que muestre <strong>cómo funciona tu solución</strong>,
                    acompañado de <strong>capturas de pantalla</strong> de la herramienta o prototipo funcionando.
                    Incluye también pruebas, resultados o un video demo para demostrar que el proyecto se realizó.
                  </p>
                  <p className="text-sm font-medium">Tu evidencia debería incluir:</p>
                  <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-0.5">
                    <li>Cómo funciona la solución, paso a paso.</li>
                    <li>Capturas de pantalla de la herramienta o prototipo funcionando.</li>
                    <li>Evidencia de que el proyecto fue construido y probado (resultados, pruebas o video demo).</li>
                    <li>Limitaciones y qué falta por hacer.</li>
                  </ul>
                </div>
                {deliverableFiles.length > 0 && (
                  <div className="space-y-2">
                    {deliverableFiles.map((f) => (
                      <div key={f.id} className="flex items-center gap-4 p-4 rounded-lg bg-muted/50 border border-success/30">
                        <File className="w-5 h-5 text-success shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium truncate">{f.name}</h5>
                          <p className="text-xs text-muted-foreground">{f.size && `${f.size} • `}{f.uploadedAt}</p>
                        </div>
                        <div className="flex gap-1">
                          {f.url && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => window.open(f.url!, '_blank')}><Download className="w-4 h-4" /></Button>}
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleRemoveDeliverableFile(f.id)}><X className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-2 border-dashed rounded-xl p-6 text-center border-muted-foreground/30 hover:border-primary/50 cursor-pointer group" onClick={handleFileUpload}>
                  <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground group-hover:text-primary" />
                  <h4 className="font-medium">{deliverableFiles.length === 0 ? 'Haz clic para subir' : 'Agregar más archivos'}</h4>
                  <p className="text-sm text-muted-foreground">PDF, DOCX, PPTX, imágenes (máx. 20MB). Puedes seleccionar varios.</p>
                </div>
              </div>


              <div className="border-t border-border" />

              {/* Rubric */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-teal-100"><ClipboardCheck className="w-5 h-5 text-teal-600" /></div><div><h4 className="font-semibold">Preguntas</h4><p className="text-xs text-muted-foreground">12 preguntas sobre cómo hiciste tu proyecto</p></div></div>
                  <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">{Object.values(rubricAnswers).filter(a => (a || '').trim().length >= 20).length}/{LIDER_IA_COMPONENTS.length}</Badge>
                </div>

                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 flex gap-3">
                  <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm space-y-1">
                    <p className="font-medium text-amber-900">Responde con sinceridad: “no lo tuve en cuenta” también es una buena respuesta.</p>
                    <p className="text-amber-800">
                      Estas preguntas describen buenas prácticas de un proyecto de IA. Es normal que en un primer
                      proyecto no hayas trabajado todas. Si un aspecto no lo consideraste, dilo y explica por qué
                      (por ejemplo: “No definimos controles de datos porque usamos datos ficticios”).
                      Eso solo baja el puntaje de ese componente; <strong>no invalida tu proyecto</strong> y es mucho
                      mejor que inventar una respuesta. Usa el ícono <HelpCircle className="w-3.5 h-3.5 inline text-amber-700" /> de cada
                      pregunta si no la entiendes.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {LIDER_IA_COMPONENTS.map(comp => {
                    const answer = rubricAnswers[comp.id] || '';
                    const isComplete = answer.trim().length >= 20;
                    const missing = Math.max(0, 20 - answer.trim().length);
                    return (
                      <Card key={comp.id} className={`border ${isComplete ? 'border-success/30 bg-success/5' : 'border-border'}`}>
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-start gap-2">
                            <Badge className={`shrink-0 text-xs ${DIMENSION_COLORS[comp.dimensionLetter]}`}>{comp.dimensionLetter} · {comp.shortName}</Badge>
                            <div className="ml-auto flex items-center gap-1">
                              {isComplete && <CheckCircle2 className="w-4 h-4 text-success shrink-0" />}
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" aria-label="Ayuda sobre esta pregunta">
                                    <HelpCircle className="w-4 h-4" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-80 text-sm space-y-3">
                                  <div>
                                    <p className="font-semibold">{comp.name}</p>
                                    <p className="text-xs text-muted-foreground">{comp.dimensionName}</p>
                                  </div>
                                  <p className="text-muted-foreground">{comp.helpText || comp.description}</p>
                                  {comp.example && (
                                    <div className="p-2 rounded-md bg-muted/60 flex gap-2">
                                      <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                      <div>
                                        <p className="text-xs font-medium">Ejemplo de respuesta</p>
                                        <p className="text-xs text-muted-foreground">{comp.example}</p>
                                      </div>
                                    </div>
                                  )}
                                  <p className="text-xs text-muted-foreground">
                                    Si no lo tuviste en cuenta, dilo y explica por qué. Es válido.
                                  </p>
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                          <p className="text-sm font-medium leading-tight">{comp.question}</p>
                          <p className="text-xs text-muted-foreground leading-snug">{comp.description}</p>
                          <Textarea value={answer} onChange={e => handleRubricAnswerChange(comp.id, e.target.value)} placeholder={comp.placeholder} rows={3} className="resize-none text-sm" />
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-xs ${isComplete ? 'text-success' : 'text-muted-foreground'}`}>{isComplete ? '✓ Completa' : `Faltan ${missing} caracteres`}</p>
                            {!answer.trim() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                                onClick={() => handleRubricAnswerChange(comp.id, 'No lo tuve en cuenta en este proyecto porque ')}
                              >
                                No lo tuve en cuenta
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-border" />

              {/* ROI */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100"><DollarSign className="w-5 h-5 text-emerald-600" /></div>
                  <h4 className="font-semibold">Análisis de ROI</h4>
                  <Button variant="outline" size="sm" className="ml-auto gap-1.5 text-xs" onClick={() => setRoiHelpOpen(true)}>
                    <HelpCircle className="w-3.5 h-3.5" />¿Cómo se calcula?
                  </Button>
                </div>
                <RoiHelpDialog open={roiHelpOpen} onOpenChange={setRoiHelpOpen} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(Object.keys(COST_LABELS) as Array<keyof ROICostStructure>).map(key => (
                    <div key={key} className="p-3 rounded-lg bg-muted/50 space-y-2">
                      <Label className="text-sm">{COST_LABELS[key].name}</Label>
                      <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span><Input type="number" min={0} value={roiCosts[key] || ''} onChange={e => setRoiCosts(p => ({ ...p, [key]: Number(e.target.value) || 0 }))} className="pl-7 text-sm" /></div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between"><h5 className="text-sm font-semibold">Beneficios</h5><Button variant="outline" size="sm" onClick={handleAddBenefit} className="text-xs">+ Agregar</Button></div>
                {roiBenefits.map((b, i) => (
                  <div key={i} className="p-3 rounded-lg bg-muted/50 space-y-2">
                    <div className="flex gap-2">
                      <Select value={b.type} onValueChange={v => handleBenefitChange(i, 'type', v)}><SelectTrigger className="flex-1 text-sm"><SelectValue /></SelectTrigger><SelectContent>{(Object.keys(BENEFIT_TYPE_LABELS) as BenefitType[]).map(bt => <SelectItem key={bt} value={bt}>{BENEFIT_TYPE_LABELS[bt]}</SelectItem>)}</SelectContent></Select>
                      {roiBenefits.length > 1 && <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleRemoveBenefit(i)}><X className="w-4 h-4" /></Button>}
                    </div>
                    {b.type !== 'not_defined' && <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">$</span><Input type="number" min={0} value={b.amount || ''} onChange={e => handleBenefitChange(i, 'amount', Number(e.target.value) || 0)} className="pl-7 text-sm" /></div>}
                    <Input value={b.description} onChange={e => handleBenefitChange(i, 'description', e.target.value)} placeholder="Descripción..." className="text-sm" />
                  </div>
                ))}
                {roiTotalCosts > 0 && (
                  <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="text-center"><p className="text-lg font-bold text-destructive">${roiTotalCosts.toLocaleString()}</p><p className="text-xs text-muted-foreground">Costos</p></div>
                    <div className="text-center"><p className="text-lg font-bold text-success">${roiTotalBenefits.toLocaleString()}</p><p className="text-xs text-muted-foreground">Beneficios</p></div>
                    <div className="text-center"><p className={`text-lg font-bold ${roiPercentage > 0 ? 'text-success' : 'text-destructive'}`}>{roiPercentage.toFixed(0)}%</p><p className="text-xs text-muted-foreground">ROI</p></div>
                    <div className="text-center"><p className="text-lg font-bold">{roiPayback === Infinity ? '∞' : `${roiPayback.toFixed(1)}`}</p><p className="text-xs text-muted-foreground">Payback (meses)</p></div>
                  </div>
                )}
              </div>

              {/* Cohort selector */}
              {!projectRow?.cohort_id && guidedEnrollments.length > 0 && (
                <div className="space-y-2 pt-4 border-t border-border">
                  <Label>Cohorte para este entregable *</Label>
                  <SearchableCombobox
                    value={submitCohortId}
                    onSelect={setSubmitCohortId}
                    placeholder="Selecciona"
                    searchPlaceholder="Buscar cohorte o ruta..."
                    options={guidedEnrollments.map(e => ({
                      value: e.cohort_id,
                      label: `Ruta: ${e.program_name} — ${e.cohort_name}`,
                      keywords: [e.program_name, e.cohort_name],
                    }))}
                  />
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={handleSaveDraft}
                  className="gap-2"
                >
                  <Save className="w-4 h-4" />{deliverableSubmitted ? 'Guardar Cambios' : 'Guardar Borrador'}
                </Button>
                {!deliverableSubmitted && (
                  <Button
                    onClick={handleSubmitDeliverable}
                    disabled={
                      deliverableFiles.length === 0 ||
                      Object.keys(rubricAnswers).filter(k => (rubricAnswers[k] || '').trim().length >= 20).length < LIDER_IA_COMPONENTS.length
                    }
                    className="gap-2 bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <Send className="w-4 h-4" />Enviar a Revisión
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ProjectDetailPage;
